import { Injectable } from '@angular/core';
import { ZraTaxCode, ZraTaxCodesService } from '@boma/supplier-portal/shared/services';
import { Store } from '@ngrx/store';
import { BehaviorSubject, map, Observable, switchMap, takeUntil, Subject, of } from 'rxjs';
import { getSelectedOrganizationInfo } from '../shared-state';

export interface TaxCodesState {
  taxCodes: ZraTaxCode[];
  loading: boolean;
  error: string | null;
}

@Injectable()
export class TaxCodesFacade {
  private readonly _entityId$: Observable<string>;
  private readonly _state$: BehaviorSubject<TaxCodesState> =
    new BehaviorSubject<TaxCodesState>({
      taxCodes: [],
      loading: false,
      error: null,
    });
  private readonly _loadCancellation$ = new Subject<void>();

  loading$ = this._state$.pipe(map((state) => state.loading));
  error$ = this._state$.pipe(map((state) => state.error));
  taxCodes$ = this._state$.pipe(map((state) => state.taxCodes));

  constructor(
    private store: Store,
    private taxCodesService: ZraTaxCodesService
  ) {
    this._entityId$ = this.store
      .select(getSelectedOrganizationInfo)
      .pipe(map((info) => info.id));
  }

  loadTaxCodes(): void {
    this._updateState({ loading: true, error: null });
    
    // Cancel any ongoing load
    this._loadCancellation$.next();
    
    this._entityId$
      .pipe(
        switchMap((entityId) => this.taxCodesService.getTaxCodes(entityId)),
        takeUntil(this._loadCancellation$)
      )
      .subscribe({
        next: (taxCodes) => {
          this._updateState({
            taxCodes,
            loading: false,
          });
        },
        error: (error) => {
          this._updateState({ error: error?.message, loading: false });
        },
      });
  }

  getTaxCodeByZraCode(zraCode: string): Observable<ZraTaxCode | undefined> {
    return this.taxCodes$.pipe(
      map(taxCodes => taxCodes.find(taxCode => taxCode.zraCode === zraCode))
    );
  }

  private _updateState(state: Partial<TaxCodesState>) {
    this._state$.next({ ...this._state$.value, ...state });
  }
} 